import { CompanyWebhookStatus } from "@freightsimple/generated-dashboard-openapi-client/models/CompanyWebhookStatus";
import { WebhookDeliveryState } from "@freightsimple/generated-dashboard-openapi-client/models/WebhookDeliveryState";
import type { WebhookDelivery } from "@freightsimple/generated-dashboard-openapi-client/models/WebhookDelivery";
import type { WebhookSummary } from "@freightsimple/generated-dashboard-openapi-client/models/WebhookSummary";

export interface WebhooksRetryEligibility {
  allowed: boolean;
  /** Why the customer can't redeliver, worded for the disabled button's tooltip. */
  reason: string | undefined;
}

/**
 * Whether the customer may redeliver this delivery from the Dashboard. Mirrors the backend's
 * rules (only a failed, given-up or replaced delivery can be re-sent, and not once any send of the
 * event got through; a root delivery may be re-sent `maxCustomerRetries` times; the webhook must
 * still exist and be active), so the button is
 * disabled with the reason instead of failing on click. The window needs no check here: only
 * deliveries within retention are listed. The backend still decides.
 */
export function webhooksRetryEligibility(
  delivery: WebhookDelivery,
  maxCustomerRetries: number,
  webhooks: WebhookSummary[],
): WebhooksRetryEligibility {
  switch (delivery.state) {
    case WebhookDeliveryState.Delivered:
      return { allowed: false, reason: "Delivered, nothing to re-send" };
    case WebhookDeliveryState.Pending:
      return {
        allowed: false,
        reason: "We're still retrying this one ourselves",
      };
    case WebhookDeliveryState.Failed:
    case WebhookDeliveryState.Exhausted:
    case WebhookDeliveryState.Superseded:
      break;
  }
  if (delivery.eventDelivered) {
    return {
      allowed: false,
      reason: "Another send of this event already reached your endpoint",
    };
  }
  if (delivery.customerRetryCount >= maxCustomerRetries) {
    return {
      allowed: false,
      reason: `You've re-sent this delivery ${maxCustomerRetries} times. If your endpoint still can't accept it, contact FreightSimple support.`,
    };
  }
  const webhook = webhooks.find(
    (w) => w.companyWebhookId === delivery.companyWebhookId,
  );
  if (webhook === undefined) {
    return {
      allowed: false,
      reason: "The webhook this was sent to no longer exists",
    };
  }
  if (webhook.status !== CompanyWebhookStatus.Active) {
    return {
      allowed: false,
      reason:
        "This webhook is paused. Contact FreightSimple to reactivate it, then re-send.",
    };
  }
  return { allowed: true, reason: undefined };
}

/**
 * A body for reading. We send JSON minified, on one line, and a customer's endpoint often
 * answers the same way; indented it can be read without scrolling sideways. Anything that is
 * not JSON is returned as it came.
 */
export function webhooksBodyForDisplay(body: string): {
  text: string;
  language: "json" | "plaintext";
} {
  try {
    return {
      text: JSON.stringify(JSON.parse(body), undefined, 2),
      language: "json",
    };
  } catch {
    return { text: body, language: "plaintext" };
  }
}
